import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { IBranch } from "../types/IHeritageConnection";
import axios from "axios";
import { SearchInput } from "components/input";
import {
    groupBranchesByShipTo,
    resolveSelectedBranchId,
} from "../../../../utils/heritageBranchGroups";

interface IBranchResponse {
    branches: IBranch[];
}

interface VendorHeritageSettings {
    branchId: number;
    branch_code: string | null;
    branch_name: string | null;
    ship_to_sequence_number?: number | null;
}

interface VendorBranchesProps {
    /** When set, branch selection is per-vendor; when null, uses company default (e.g. admin flow). */
    vendorId?: number | null;
    /** Create-vendor flow: hold selection locally until the vendor row exists. */
    pendingBranchId?: number | null;
    onPendingBranchChange?: (branchId: number) => void;
    branchSelectionDisabled?: boolean;
}

const EMPTY_BRANCHES: IBranch[] = [];

function branchMatchesQuery(branch: IBranch, q: string): boolean {
    const n = (branch.branch_name ?? "").toLowerCase();
    const c = (branch.branch_code ?? "").toLowerCase();
    return n.includes(q) || c.includes(q);
}

function pinSelectedBranchFirst(branches: IBranch[], selectedId: number | undefined): IBranch[] {
    if (selectedId == null) return branches;
    const selected = branches.find((b) => b.id === selectedId);
    if (!selected) return branches;
    return [selected, ...branches.filter((b) => b.id !== selectedId)];
}

function computeVisibleBranches(branches: IBranch[], branchSearch: string, selectedId: number | undefined): IBranch[] {
    const q = branchSearch.trim().toLowerCase();
    if (!q) {
        return pinSelectedBranchFirst(branches, selectedId);
    }

    const filtered = branches.filter((b) => branchMatchesQuery(b, q));
    const selected = selectedId != null ? branches.find((b) => b.id === selectedId) : undefined;
    if (selected && !filtered.some((b) => b.id === selected.id)) {
        return [selected, ...filtered];
    }
    return pinSelectedBranchFirst(filtered, selectedId);
}

const VendorBranches = ({
    vendorId,
    pendingBranchId,
    onPendingBranchChange,
    branchSelectionDisabled = false,
}: VendorBranchesProps) => {
    const queryClient = useQueryClient();
    const useVendorBranch = vendorId != null;
    const usePendingBranch = !useVendorBranch && onPendingBranchChange != null;
    const [branchSearch, setBranchSearch] = useState("");
    const [saveError, setSaveError] = useState<string | null>(null);

    const {
        data: branchesData,
        isLoading: isBranchesLoading,
        isError: isBranchesError,
    } = useQuery<IBranchResponse>(
        ["heritage-branches"],
        () =>
            axios
                .get<IBranchResponse>(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-branches`)
                .then((res) => res.data)
    );

    const { data: vendorSettings } = useQuery<VendorHeritageSettings>(
        ["heritage-vendor-settings", vendorId],
        () =>
            axios
                .get<VendorHeritageSettings>(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/${vendorId}/heritage/settings`)
                .then((res) => res.data),
        { enabled: useVendorBranch && !!vendorId }
    );

    /**
     * A save that fails silently leaves the picker showing a branch that was
     * never stored, and the next order routes somewhere the user did not choose.
     */
    const saveFailed = (fallback: string) => (error: unknown) => {
        const message =
            axios.isAxiosError(error) && typeof error.response?.data?.message === "string"
                ? error.response.data.message
                : fallback;
        setSaveError(message);
    };

    const setCompanyDefaultMutation = useMutation(
        (branchId: number) =>
            axios.put(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/branches/${branchId}/set-default`),
        {
            onMutate: () => setSaveError(null),
            onSuccess: () => {
                queryClient.invalidateQueries(["heritage-branches"]);
            },
            onError: saveFailed("Could not save your default branch. Try again."),
        }
    );

    const setVendorBranchMutation = useMutation(
        (branchId: number) =>
            axios.put(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/${vendorId}/heritage/settings`, { branchId }),
        {
            onMutate: () => setSaveError(null),
            onSuccess: () => {
                queryClient.invalidateQueries(["heritage-vendor-settings", vendorId]);
                queryClient.invalidateQueries(["heritage-branches"]);
            },
            onError: saveFailed("Could not save this vendor's branch. Try again."),
        }
    );

    const branches = branchesData?.branches ?? EMPTY_BRANCHES;
    const selectedId = resolveSelectedBranchId(branches, {
        vendorBranchId: useVendorBranch ? vendorSettings?.branchId : null,
        pendingBranchId: usePendingBranch ? pendingBranchId : null,
    });
    /** What the create-vendor flow starts from before the user has touched anything. */
    const fallbackDefaultId = resolveSelectedBranchId(branches);

    const visibleBranches = computeVisibleBranches(branches, branchSearch, selectedId);
    // Grouped after filtering, so a location whose branches all fail the search
    // does not leave an empty heading behind.
    const shipToGroups = groupBranchesByShipTo(visibleBranches);

    useEffect(() => {
        if (usePendingBranch && pendingBranchId == null && fallbackDefaultId != null) {
            onPendingBranchChange!(fallbackDefaultId);
        }
    }, [usePendingBranch, pendingBranchId, fallbackDefaultId, onPendingBranchChange]);

    if (isBranchesLoading) return <p>Loading branches...</p>;
    if (isBranchesError) return <p>Failed to fetch branches.</p>;

    const setBranch = (branchId: number) => {
        if (branchSelectionDisabled) return;
        if (useVendorBranch) {
            setVendorBranchMutation.mutate(branchId);
        } else if (usePendingBranch) {
            setSaveError(null);
            onPendingBranchChange!(branchId);
        } else {
            setCompanyDefaultMutation.mutate(branchId);
        }
    };

    const radioGroupName = useVendorBranch
        ? `vendor-branch-${vendorId}`
        : usePendingBranch
          ? "vendor-branch-pending"
          : "default-branch";

    const q = branchSearch.trim().toLowerCase();

    return (
        <div className="text-sm text-gray-700 bg-gray-50 border rounded p-3 space-y-2">
            {branchSelectionDisabled && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 m-0">
                    Branch selection is disabled while a catalog download is in progress.
                </p>
            )}
            {saveError && (
                <p
                    role="alert"
                    className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-md px-2 py-1.5 m-0"
                >
                    {saveError}
                </p>
            )}
            {(useVendorBranch || usePendingBranch) && (
                <p className="text-gray-600 text-xs mb-2">This branch is used for this vendor’s Heritage items and orders.</p>
            )}
            <p className="text-gray-600 text-xs m-0">
                Branches are grouped by the ship-to location they deliver from. Heritage needs both, so
                choosing a branch also chooses its location.
            </p>
            <SearchInput
                search
                placeholder="Search branches by name or code…"
                value={branchSearch}
                onChange={(e) => setBranchSearch((e.target as HTMLInputElement).value)}
                aria-label="Filter branches"
            />
            <div className="max-h-[min(24rem,50vh)] overflow-y-auto -mx-1 px-1 mt-1">
                {shipToGroups.length === 0 ? (
                    <p className="text-gray-500 text-xs py-4 text-center">
                        {branches.length === 0
                            ? "No branches yet. Use Check for Branches once your Heritage credentials are validated."
                            : "No branches match your search."}
                    </p>
                ) : (
                    shipToGroups.map((group) => (
                        <section key={group.key || "ungrouped"} className="mb-3 last:mb-0">
                            <header className="sticky top-0 z-[1] bg-gray-50 flex flex-wrap items-baseline gap-x-2 py-1.5 border-b border-gray-200">
                                <h4 className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-700 min-w-0 break-words">
                                    {group.label}
                                </h4>
                                {group.sequenceNumber && (
                                    <span className="text-[10px] text-gray-500 shrink-0">
                                        Ship-to {group.sequenceNumber}
                                    </span>
                                )}
                            </header>
                            {group.branches.map((branch) => {
                                const isSelected = selectedId === branch.id;
                                const pinned = q.length > 0 && isSelected && !branchMatchesQuery(branch, q);
                                return (
                                    <label
                                        key={branch.id}
                                        className={`flex justify-between items-center gap-3 border-b py-2 min-h-[44px] -mx-2 px-2 rounded-md transition-colors ${
                                            branchSelectionDisabled ? "cursor-not-allowed opacity-75" : "cursor-pointer"
                                        } ${
                                            isSelected
                                                ? "bg-orange-50 border-orange-100 ring-1 ring-orange-200/80"
                                                : pinned
                                                  ? "bg-amber-50/80 border-amber-100"
                                                  : ""
                                        }`}
                                    >
                                        <div className="flex flex-col min-w-0 pr-2">
                                            {pinned && (
                                                <span className="text-[10px] uppercase tracking-wide text-amber-800 font-medium mb-0.5">
                                                    Current selection
                                                </span>
                                            )}
                                            <span className="font-semibold truncate">{branch.branch_name}</span>
                                            <span className="text-gray-500 truncate">{branch.branch_code}</span>
                                        </div>
                                        <input
                                            type="radio"
                                            name={radioGroupName}
                                            value={branch.id}
                                            checked={isSelected}
                                            disabled={branchSelectionDisabled}
                                            onChange={() => setBranch(branch.id)}
                                            title={
                                                branchSelectionDisabled
                                                    ? "Branch selection is disabled while catalog download is in progress"
                                                    : undefined
                                            }
                                            className="form-radio h-4 w-4 shrink-0 border-gray-400 text-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-1 touch-manipulation disabled:cursor-not-allowed"
                                        />
                                    </label>
                                );
                            })}
                        </section>
                    ))
                )}
            </div>
        </div>
    );
};

export default VendorBranches;
